import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_RATES, convertMinor, getActiveRates, mergeRates, setActiveRates, type FxRates } from "@/lib/fx";
import { API_BASE_URL } from "@/lib/api";
import { loadOfferPreferences, saveOfferPreferences, type OfferPreferences } from "@/lib/offer-preferences";

export type Locale = "en" | "de" | "es" | "pl";
export type Currency = "EUR" | "USD" | "GBP" | "PLN" | "CHF" | "CAD" | "AUD" | "JPY";
export type Theme = "dark" | "light";

interface SettingsCtx {
  locale: Locale;
  currency: Currency;
  theme: Theme;
  setLocale: (l: Locale) => void;
  setCurrency: (c: Currency) => void;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  /** Homepage region/shop preferences. null = never saved (show intro dialog). */
  offerPrefs: OfferPreferences | null;
  /** True once preferences have been read from localStorage. */
  offerPrefsLoaded: boolean;
  setOfferPrefs: (p: OfferPreferences) => void;
  t: (key: string) => string;
  formatPrice: (cents: number | null | undefined, currency?: string) => string;
  /** Convert minor units into the visitor's currency (same rates as formatPrice). */
  convertPrice: (cents: number, fromCurrency?: string | null) => number | null;
  /** Active reference rates (units per 1 EUR). */
  fxRates: FxRates;
}

/* ============ Translations ============ */

const EN: Record<string, string> = {
  "nav.games": "Games", "nav.deals": "Top Deals", "nav.genres": "Genres", "nav.shops": "Shops", "nav.giftcards": "Gift Cards",
  "nav.login": "Login", "nav.wishlist": "Wishlist", "nav.menu": "Menu",
  "account.title": "Your account", "account.subtitle": "Manage your NexKey account and saved games.",
  "account.email": "Email", "account.password": "Password", "account.confirm": "Confirm password",
  "account.password.hint": "At least 8 characters.",
  "account.login.title": "Sign in", "account.login.subtitle": "Access your saved games from any device.",
  "account.login.cta": "Sign in", "account.login.noAccount": "No account yet?",
  "account.register.title": "Create account", "account.register.subtitle": "It's free and takes a few seconds.",
  "account.register.cta": "Create account", "account.register.haveAccount": "Already have an account?",
  "account.logout": "Log out", "account.loading": "Loading…", "account.memberSince": "Member since",
  "account.error.required": "Please enter your email and password.",
  "account.error.short": "Password must be at least 8 characters.",
  "account.error.mismatch": "Passwords do not match.",
  "account.error.login": "Login failed.", "account.error.register": "Registration failed.",
  "wishlist.count": "{count} saved games", "wishlist.empty": "You haven't saved any games yet.",
  "wishlist.syncHint": "Sign in to sync your wishlist",
  "wishlist.sort": "Sort", "wishlist.sort.discount": "Biggest discount", "wishlist.sort.priceAsc": "Lowest price",
  "wishlist.sort.priceDesc": "Highest price", "wishlist.sort.title": "Title A–Z",
  "wishlist.open": "Open game", "wishlist.remove": "Remove", "wishlist.bestOffer": "Best offer",
  "wishlist.reference": "Steam price", "wishlist.stores": "{count} stores", "wishlist.store": "1 store",
  "wishlist.noOffers": "No offers yet", "wishlist.alertActive": "Alert: below {price}", "wishlist.alertHit": "Target reached",
  "account.dashboard.wishlist": "Wishlist games", "account.dashboard.alerts": "Active price alerts",
  "account.dashboard.viewWishlist": "Open wishlist", "account.dashboard.viewAlerts": "Manage price alerts",
  "account.settings": "Account settings", "account.settings.hint": "Language, currency and theme are stored on this device.",
  "account.settings.language": "Language", "account.settings.currency": "Currency", "account.settings.theme": "Theme",
  "account.settings.dark": "Dark", "account.settings.light": "Light", "account.settings.soon": "Password change and email updates are coming soon.",
  "account.back": "Back to account",
  "alerts.title": "Price alerts", "alerts.subtitle": "We'll let you know when a game drops below your target price.",
  "alerts.button": "Price alert", "alerts.button.active": "Alert active",
  "alerts.dialog.title": "Set a price alert", "alerts.dialog.current": "Current best price",
  "alerts.dialog.currency": "Currency", "alerts.dialog.target": "Notify me when the price drops below",
  "alerts.dialog.create": "Create alert", "alerts.dialog.save": "Save changes", "alerts.dialog.delete": "Delete alert",
  "alerts.dialog.disable": "Disable", "alerts.dialog.activeNote": "Your alert is active — target {price}.",
  "alerts.dialog.noticeSoon": "Email notifications are coming soon; your alert is saved already.",
  "alerts.dialog.invalid": "Enter a valid price.", "alerts.dialog.saved": "Alert saved.", "alerts.dialog.removed": "Alert removed.",
  "alerts.loginHint": "Sign in to create price alerts.",
  "alerts.current": "Current price", "alerts.target": "Target", "alerts.difference": "Difference",
  "alerts.status": "Status", "alerts.status.active": "Active", "alerts.status.hit": "Target reached", "alerts.status.paused": "Paused",
  "alerts.edit": "Edit", "alerts.delete": "Delete", "alerts.openGame": "Open game",
  "alerts.empty": "You don't have any price alerts yet.", "alerts.empty.cta": "Browse games",
  "alerts.count": "{count} active alerts", "alerts.noPrice": "No price yet",
  "search.placeholder": "Search for a game…",
  "hero.badge": "Real-time price comparison · Updated daily",
  "hero.title.a": "Compare Game Prices.", "hero.title.b": "Find the Best Deal.", "hero.title.c": "",
  "hero.subtitle": "One search across every major store. Instantly spot the cheapest legit key and save on every game you buy.",
  "hero.searchBtn": "Search",
  "hero.browseGames": "Browse Games",
  "hero.bestDeals": "Today's Best Deals",
  "hero.searchPlaceholder": "Search {count}+ games instantly…",
  "hero.popular": "Popular:",
  "hero.stat.games": "Games", "hero.stat.stores": "Stores", "hero.stat.offers": "Live offers",
  "home.honest.title": "Honest game key price comparison",
  "home.honest.body": "NexKey.gg tracks the official Steam reference price for every imported game and compares it against offers from trusted key shops. Steam is our source of truth — titles, descriptions, screenshots and base prices come straight from the Steam Storefront API. We are not affiliated with Valve Corporation.",
  "home.honest.link": "Read the Steam disclaimer",
  "section.trending": "Trending now", "section.new": "New releases",
  "section.deals": "Best deals", "section.genres": "Popular genres", "section.shops": "Featured shops",
  "empty.games": "No games imported yet. Start a Steam import from the admin dashboard.",
  "empty.offers": "No shop offers available yet.",
  "empty.steamPrice": "Steam reference price currently unavailable.",
  "empty.deals": "No deals to show yet — add shop offers from the admin dashboard.",
  "empty.shops": "No shops configured yet.",
  "empty.genres": "No genres yet — they are created automatically when games are imported.",
  "offer.viewDeal": "View deal", "offer.bestPrice": "Best price",
  "offer.steamPrice": "Steam price", "offer.savings": "You save",
  "loading": "Loading…", "common.error": "Something went wrong.",
  "common.retry": "Try again", "common.goHome": "Go home",

  "footer.tagline": "Independent price comparison for game keys. We track Steam reference prices daily and compare them against trusted key shops so you always pay the fair price.",
  "footer.explore": "Explore", "footer.company": "Company", "footer.disclosures": "Disclosures",
  "footer.about": "About", "footer.contact": "Contact", "footer.privacy": "Privacy", "footer.terms": "Terms",
  "footer.affiliate": "Affiliate disclosure", "footer.steam": "Steam disclaimer",
  "footer.copyright": "Not affiliated with Valve Corporation. Steam and the Steam logo are trademarks of Valve.",


  "game.breadcrumb.home": "Home", "game.breadcrumb.games": "Games",
  "game.meta.releaseDate": "Release date", "game.meta.developer": "Developer",
  "game.meta.publisher": "Publisher", "game.meta.platforms": "Platforms",
  "game.meta.metacritic": "Metacritic", "game.meta.genres": "Genres", "game.meta.tags": "Steam tags",
  "game.action.wishlist": "Wishlist", "game.action.share": "Share",
  "game.action.comparePrices": "Compare prices",
  "game.action.readMore": "Read more", "game.action.readLess": "Show less",
  "game.action.buy": "Buy", "game.action.buyNow": "Buy now", "game.action.buyOnSteam": "Buy on Steam",
  "game.section.priceComparison": "Price comparison",
  "game.section.priceComparison.sub": "Sorted by lowest price. Includes Steam and every listed key shop.",
  "game.section.quickFacts": "Quick facts",
  "game.section.about": "About this game",
  "game.section.steamDesc": "Official Steam Description",
  "game.section.media": "Screenshots & videos",
  "game.section.features": "Game features",
  "game.section.languages": "Supported languages",
  "game.section.languages.showAll": "Show all languages",
  "game.section.languages.showLess": "Show less",
  "game.section.additional": "Additional information",
  "game.section.system": "System requirements",
  "game.additional.metacritic": "Metacritic score",
  "game.additional.controller": "Controller support",
  "game.additional.achievements": "Achievements",
  "game.additional.dlc": "DLC available",
  "game.additional.packages": "Package editions",
  "game.additional.website": "Official website",
  "game.additional.legal": "Legal notice",
  "game.table.language": "Language", "game.table.interface": "Interface",
  "game.table.audio": "Full audio", "game.table.subtitles": "Subtitles",
  "game.verified": "Verified stores",
  "game.priceNote": "Discounts compared against Steam reference price.",
  "game.affiliate": "Affiliate disclosure",
  "history.title": "Price History", "history.best": "Best Price", "history.current": "Current Price",
  "history.lowest": "Lowest Price", "history.since": "Tracked Since", "history.shops": "Tracked shops",
  "history.range.7d": "7 Days", "history.range.30d": "30 Days", "history.range.90d": "90 Days", "history.range.all": "All Time",
  "history.empty": "No price history recorded for this game yet.",
  "history.error": "Price history is currently unavailable.",
  "game.similar.title": "You may also like",
  "game.similar.sub": "Players interested in this game also viewed",
  "game.similar.empty": "No recommendations available yet.",
  "game.similar.bestDeal": "Best deal",
  "game.quick.metacritic": "Metacritic", "game.quick.release": "Release date",
  "game.quick.platform": "Platform / DRM", "game.quick.languages": "Languages",
  "game.quick.achievements": "Achievements", "game.quick.controller": "Controller",
  "game.quick.dlc": "DLC", "game.quick.developer": "Developer", "game.quick.publisher": "Publisher",
  "game.tabs.overview": "Overview", "game.tabs.system": "System Requirements",
  "game.tabs.languages": "Languages", "game.tabs.features": "Features",
  "game.tabs.additional": "Additional Info",

  "reqs.minimum": "Minimum", "reqs.recommended": "Recommended",
  "reqs.empty": "System requirements for this platform are not available.",
  "reqs.label.os": "Operating System", "reqs.label.processor": "Processor",
  "reqs.label.memory": "Memory", "reqs.label.graphics": "Graphics",
  "reqs.label.directx": "DirectX", "reqs.label.storage": "Storage",
  "reqs.label.sound": "Sound Card", "reqs.label.network": "Network",
  "reqs.label.vr": "VR Support", "reqs.label.notes": "Additional Notes",

  "price.rank": "#", "price.store": "Store", "price.edition": "Edition",
  "price.platform": "Platform", "price.region": "Region", "price.drm": "DRM",
  "price.delivery": "Delivery", "price.price": "Price", "price.savings": "Savings",
  "price.bestDeal": "Best deal", "price.officialStore": "Official store",
  "shopType.official-store": "Official Store", "shopType.authorized-retailer": "Authorized Retailer",
  "shopType.marketplace": "Marketplace", "shopType.key-reseller": "Key Reseller",
  "price.instantDelivery": "Available instantly",
  "price.unknownDrm": "Unknown DRM",
  "price.lowest": "Lowest price", "price.steamPrice": "Steam price",
  "price.youSave": "You save",
  "price.topOffers": "Top offers", "price.activeEdition": "For the selected edition", "game.dlcs": "DLCs", "game.dlc.badge": "DLC / Add-on", "game.dlc.requires": "Requires", "game.dlc.from": "from", "game.dlc.offers": "offers",
  "price.viewAllOffers": "View all offers ({count})",
  "price.updatedNote": "Prices updated automatically across every verified shop.",
  "price.verifiedPartner": "Verified partner",
  "price.from": "from",
  "price.regular": "Regular shop price",
  "price.withCode": "With code",
  "price.couponBadge": "coupon",
  "price.copyCode": "Copy code",
  "price.copied": "Copied!",
  "price.couponRequired": "Coupon required",
  "prefs.title": "Customize your price results",
  "prefs.subtitle": "Choose the regions and shops you want to see. You can change these preferences later.",
  "prefs.regions": "Regions", "prefs.shops": "Shops", "prefs.all": "All",
  "prefs.unknown.title": "What does “Unknown” mean?",
  "prefs.unknown.body": "Unknown means we could not reliably determine the key's region from the available data. An Unknown offer may still be Global, Europe, America, China or another region. We recommend keeping Unknown enabled so you don't miss potentially relevant offers. Always check the product page and the seller's activation restrictions before purchasing.",
  "prefs.save": "Save preferences",
  "prefs.continueAll": "Continue with all regions & shops",
  "prefs.open": "Regions & shops",
  "prefs.note": "Stored on this device only.",
};

const DE: Record<string, string> = {
  "nav.games": "Spiele", "nav.deals": "Top Angebote", "nav.genres": "Genres", "nav.shops": "Shops", "nav.giftcards": "Geschenkkarten",
  "nav.login": "Anmelden", "nav.wishlist": "Merkliste", "nav.menu": "Menü",
  "account.title": "Dein Konto", "account.subtitle": "Verwalte dein NexKey-Konto und gemerkte Spiele.",
  "account.email": "E-Mail", "account.password": "Passwort", "account.confirm": "Passwort bestätigen",
  "account.password.hint": "Mindestens 8 Zeichen.",
  "account.login.title": "Anmelden", "account.login.subtitle": "Greife von jedem Gerät auf deine Merkliste zu.",
  "account.login.cta": "Anmelden", "account.login.noAccount": "Noch kein Konto?",
  "account.register.title": "Konto erstellen", "account.register.subtitle": "Kostenlos und in wenigen Sekunden erledigt.",
  "account.register.cta": "Konto erstellen", "account.register.haveAccount": "Du hast bereits ein Konto?",
  "account.logout": "Abmelden", "account.loading": "Lädt…", "account.memberSince": "Mitglied seit",
  "account.error.required": "Bitte E-Mail und Passwort eingeben.",
  "account.error.short": "Das Passwort muss mindestens 8 Zeichen haben.",
  "account.error.mismatch": "Die Passwörter stimmen nicht überein.",
  "account.error.login": "Anmeldung fehlgeschlagen.", "account.error.register": "Registrierung fehlgeschlagen.",
  "wishlist.count": "{count} gemerkte Spiele", "wishlist.empty": "Du hast noch keine Spiele gemerkt.",
  "wishlist.syncHint": "Anmelden, um die Merkliste zu synchronisieren",
  "wishlist.sort": "Sortieren", "wishlist.sort.discount": "Größter Rabatt", "wishlist.sort.priceAsc": "Niedrigster Preis",
  "wishlist.sort.priceDesc": "Höchster Preis", "wishlist.sort.title": "Titel A–Z",
  "wishlist.open": "Spiel öffnen", "wishlist.remove": "Entfernen", "wishlist.bestOffer": "Bestes Angebot",
  "wishlist.reference": "Steam-Preis", "wishlist.stores": "{count} Shops", "wishlist.store": "1 Shop",
  "wishlist.noOffers": "Noch keine Angebote", "wishlist.alertActive": "Alarm: unter {price}", "wishlist.alertHit": "Ziel erreicht",
  "account.dashboard.wishlist": "Gemerkte Spiele", "account.dashboard.alerts": "Aktive Preisalarme",
  "account.dashboard.viewWishlist": "Merkliste öffnen", "account.dashboard.viewAlerts": "Preisalarme verwalten",
  "account.settings": "Kontoeinstellungen", "account.settings.hint": "Sprache, Währung und Design werden auf diesem Gerät gespeichert.",
  "account.settings.language": "Sprache", "account.settings.currency": "Währung", "account.settings.theme": "Design",
  "account.settings.dark": "Dunkel", "account.settings.light": "Hell", "account.settings.soon": "Passwortänderung und E-Mail-Updates folgen in Kürze.",
  "account.back": "Zurück zum Konto",
  "alerts.title": "Preisalarme", "alerts.subtitle": "Wir informieren dich, wenn ein Spiel unter deinen Zielpreis fällt.",
  "alerts.button": "Preisalarm", "alerts.button.active": "Alarm aktiv",
  "alerts.dialog.title": "Preisalarm einrichten", "alerts.dialog.current": "Aktuell bester Preis",
  "alerts.dialog.currency": "Währung", "alerts.dialog.target": "Benachrichtige mich, wenn der Preis fällt unter",
  "alerts.dialog.create": "Alarm erstellen", "alerts.dialog.save": "Änderungen speichern", "alerts.dialog.delete": "Alarm löschen",
  "alerts.dialog.disable": "Deaktivieren", "alerts.dialog.activeNote": "Dein Alarm ist aktiv — Ziel {price}.",
  "alerts.dialog.noticeSoon": "E-Mail-Benachrichtigungen folgen bald; dein Alarm ist bereits gespeichert.",
  "alerts.dialog.invalid": "Bitte einen gültigen Preis eingeben.", "alerts.dialog.saved": "Alarm gespeichert.", "alerts.dialog.removed": "Alarm entfernt.",
  "alerts.loginHint": "Melde dich an, um Preisalarme zu erstellen.",
  "alerts.current": "Aktueller Preis", "alerts.target": "Ziel", "alerts.difference": "Differenz",
  "alerts.status": "Status", "alerts.status.active": "Aktiv", "alerts.status.hit": "Ziel erreicht", "alerts.status.paused": "Pausiert",
  "alerts.edit": "Bearbeiten", "alerts.delete": "Löschen", "alerts.openGame": "Spiel öffnen",
  "alerts.empty": "Du hast noch keine Preisalarme.", "alerts.empty.cta": "Spiele entdecken",
  "alerts.count": "{count} aktive Alarme", "alerts.noPrice": "Noch kein Preis",
  "search.placeholder": "Spiel suchen…",
  "hero.badge": "Echtzeit-Preisvergleich · Täglich aktualisiert",
  "hero.title.a": "Spielepreise vergleichen.", "hero.title.b": "Den besten Deal finden.", "hero.title.c": "",
  "hero.subtitle": "Eine Suche über alle großen Stores. Finde sofort den günstigsten legalen Key und spare bei jedem Spielekauf.",
  "hero.searchBtn": "Suchen",
  "hero.browseGames": "Spiele durchsuchen",
  "hero.bestDeals": "Beste Deals heute",
  "hero.searchPlaceholder": "Durchsuche über {count} Spiele…",
  "hero.popular": "Beliebt:",
  "hero.stat.games": "Spiele", "hero.stat.stores": "Shops", "hero.stat.offers": "Aktive Angebote",
  "home.honest.title": "Ehrlicher Game-Key-Preisvergleich",
  "home.honest.body": "NexKey.gg verfolgt für jedes importierte Spiel den offiziellen Steam-Referenzpreis und vergleicht ihn mit Angeboten aus vertrauenswürdigen Key-Shops. Steam ist unsere Quelle der Wahrheit – Titel, Beschreibungen, Screenshots und Basispreise kommen direkt von der Steam-Storefront-API. Wir sind nicht mit der Valve Corporation verbunden.",
  "home.honest.link": "Steam-Hinweis lesen",
  "section.trending": "Im Trend", "section.new": "Neuerscheinungen",
  "section.deals": "Top-Angebote", "section.genres": "Beliebte Genres", "section.shops": "Empfohlene Shops",
  "empty.games": "Noch keine Spiele importiert. Starte einen Steam-Import im Admin-Bereich.",
  "empty.offers": "Noch keine Shop-Angebote verfügbar.",
  "empty.steamPrice": "Steam-Referenzpreis aktuell nicht verfügbar.",
  "empty.deals": "Noch keine Deals – füge Shop-Angebote im Admin-Bereich hinzu.",
  "empty.shops": "Noch keine Shops eingerichtet.",
  "empty.genres": "Noch keine Genres – werden beim Import automatisch erstellt.",
  "offer.viewDeal": "Zum Deal", "offer.bestPrice": "Bester Preis",
  "offer.steamPrice": "Steam-Preis", "offer.savings": "Du sparst",
  "loading": "Lädt…", "common.error": "Etwas ist schiefgelaufen.",
  "common.retry": "Erneut versuchen", "common.goHome": "Zur Startseite",

  "footer.tagline": "Unabhängiger Preisvergleich für Game-Keys. Wir verfolgen Steam-Referenzpreise täglich und vergleichen sie mit vertrauenswürdigen Key-Shops – damit du immer den fairen Preis zahlst.",
  "footer.explore": "Entdecken", "footer.company": "Unternehmen", "footer.disclosures": "Hinweise",
  "footer.about": "Über uns", "footer.contact": "Kontakt", "footer.privacy": "Datenschutz", "footer.terms": "AGB",
  "footer.affiliate": "Affiliate-Hinweis", "footer.steam": "Steam-Hinweis",
  "footer.copyright": "Nicht mit der Valve Corporation verbunden. Steam und das Steam-Logo sind Marken von Valve.",

  "game.breadcrumb.home": "Start", "game.breadcrumb.games": "Spiele",
  "game.meta.releaseDate": "Erscheinungsdatum", "game.meta.developer": "Entwickler",
  "game.meta.publisher": "Publisher", "game.meta.platforms": "Plattformen",
  "game.meta.metacritic": "Metacritic", "game.meta.genres": "Genres", "game.meta.tags": "Steam-Tags",
  "game.action.wishlist": "Merken", "game.action.share": "Teilen",
  "game.action.comparePrices": "Preise vergleichen",
  "game.action.readMore": "Mehr anzeigen", "game.action.readLess": "Weniger anzeigen",
  "game.action.buy": "Kaufen", "game.action.buyNow": "Jetzt kaufen", "game.action.buyOnSteam": "Bei Steam kaufen",
  "game.section.priceComparison": "Preisvergleich",
  "game.section.priceComparison.sub": "Sortiert nach niedrigstem Preis. Enthält Steam und alle gelisteten Key-Shops.",
  "game.section.quickFacts": "Kurzinfo",
  "game.section.about": "Über dieses Spiel",
  "game.section.steamDesc": "Offizielle Steam-Beschreibung",
  "game.section.media": "Screenshots & Videos",
  "game.section.features": "Spiel-Features",
  "game.section.languages": "Unterstützte Sprachen",
  "game.section.languages.showAll": "Alle Sprachen anzeigen",
  "game.section.languages.showLess": "Weniger anzeigen",
  "game.section.additional": "Weitere Informationen",
  "game.section.system": "Systemanforderungen",
  "game.additional.metacritic": "Metacritic-Wertung",
  "game.additional.controller": "Controller-Unterstützung",
  "game.additional.achievements": "Erfolge",
  "game.additional.dlc": "Verfügbare DLCs",
  "game.additional.packages": "Editionen / Pakete",
  "game.additional.website": "Offizielle Website",
  "game.additional.legal": "Rechtlicher Hinweis",
  "game.table.language": "Sprache", "game.table.interface": "Oberfläche",
  "game.table.audio": "Vollständige Audio", "game.table.subtitles": "Untertitel",
  "game.verified": "Verifizierte Shops",
  "game.priceNote": "Rabatte werden mit dem Steam-Referenzpreis verglichen.",
  "game.affiliate": "Affiliate-Hinweis",
  "history.title": "Preisverlauf", "history.best": "Bester Preis", "history.current": "Aktueller Preis",
  "history.lowest": "Tiefster Preis", "history.since": "Erfasst seit", "history.shops": "Erfasste Shops",
  "history.range.7d": "7 Tage", "history.range.30d": "30 Tage", "history.range.90d": "90 Tage", "history.range.all": "Gesamt",
  "history.empty": "Für dieses Spiel wurde noch kein Preisverlauf erfasst.",
  "history.error": "Der Preisverlauf ist derzeit nicht verfügbar.",
  "game.similar.title": "Das könnte dir auch gefallen",
  "game.similar.sub": "Spieler, die dieses Spiel interessiert, haben auch angesehen",
  "game.similar.empty": "Noch keine Empfehlungen verfügbar.",
  "game.similar.bestDeal": "Top-Deal",
  "game.quick.metacritic": "Metacritic", "game.quick.release": "Release",
  "game.quick.platform": "Plattform / DRM", "game.quick.languages": "Sprachen",
  "game.quick.achievements": "Erfolge", "game.quick.controller": "Controller",
  "game.quick.dlc": "DLC", "game.quick.developer": "Entwickler", "game.quick.publisher": "Publisher",
  "game.tabs.overview": "Übersicht", "game.tabs.system": "Systemanforderungen",
  "game.tabs.languages": "Sprachen", "game.tabs.features": "Features",
  "game.tabs.additional": "Weitere Infos",

  "reqs.minimum": "Minimum", "reqs.recommended": "Empfohlen",
  "reqs.empty": "Systemanforderungen für diese Plattform sind nicht verfügbar.",
  "reqs.label.os": "Betriebssystem", "reqs.label.processor": "Prozessor",
  "reqs.label.memory": "Arbeitsspeicher", "reqs.label.graphics": "Grafik",
  "reqs.label.directx": "DirectX", "reqs.label.storage": "Speicherplatz",
  "reqs.label.sound": "Soundkarte", "reqs.label.network": "Netzwerk",
  "reqs.label.vr": "VR-Unterstützung", "reqs.label.notes": "Zusätzliche Hinweise",

  "price.rank": "#", "price.store": "Shop", "price.edition": "Edition",
  "price.platform": "Plattform", "price.region": "Region", "price.drm": "DRM",
  "price.delivery": "Lieferung", "price.price": "Preis", "price.savings": "Ersparnis",
  "price.bestDeal": "Top-Deal", "price.officialStore": "Offizieller Shop",
  "shopType.official-store": "Offizieller Store", "shopType.authorized-retailer": "Autorisierter Händler",
  "shopType.marketplace": "Marktplatz", "shopType.key-reseller": "Key-Reseller",
  "price.instantDelivery": "Sofort verfügbar",
  "price.unknownDrm": "Unbekanntes DRM",
  "price.lowest": "Niedrigster Preis", "price.steamPrice": "Steam-Preis",
  "price.youSave": "Du sparst",
  "price.topOffers": "Top-Angebote", "price.activeEdition": "Für die ausgewählte Edition", "game.dlcs": "DLCs", "game.dlc.badge": "DLC / Erweiterung", "game.dlc.requires": "Benötigt", "game.dlc.from": "ab", "game.dlc.offers": "Angebote",
  "price.viewAllOffers": "Alle Angebote anzeigen ({count})",
  "price.updatedNote": "Preise werden automatisch bei allen verifizierten Shops aktualisiert.",
  "price.verifiedPartner": "Verifizierter Partner",
  "price.from": "ab",
  "price.regular": "Regulärer Shop-Preis",
  "price.withCode": "Mit Code",
  "price.couponBadge": "Gutschein",
  "price.copyCode": "Code kopieren",
  "price.copied": "Kopiert!",
  "price.couponRequired": "Gutschein erforderlich",
  "prefs.title": "Passe deine Preisergebnisse an",
  "prefs.subtitle": "Wähle die Regionen und Shops, die du sehen möchtest. Du kannst diese Einstellungen später ändern.",
  "prefs.regions": "Regionen", "prefs.shops": "Shops", "prefs.all": "Alle",
  "prefs.unknown.title": "Was bedeutet „Unbekannt“?",
  "prefs.unknown.body": "Unbekannt bedeutet, dass wir die Region des Keys anhand der verfügbaren Daten nicht zuverlässig bestimmen konnten. Ein Unbekannt-Angebot kann trotzdem Global, Europa, Amerika, China oder eine andere Region sein. Wir empfehlen, Unbekannt aktiviert zu lassen, damit du keine relevanten Angebote verpasst. Prüfe vor dem Kauf immer die Produktseite und die Aktivierungsbeschränkungen des Verkäufers.",
  "prefs.save": "Einstellungen speichern",
  "prefs.continueAll": "Mit allen Regionen & Shops fortfahren",
  "prefs.open": "Regionen & Shops",
  "prefs.note": "Nur auf diesem Gerät gespeichert.",
};

const ES: Record<string, string> = {
  "nav.games": "Juegos", "nav.deals": "Mejores ofertas", "nav.genres": "Géneros", "nav.shops": "Tiendas", "nav.giftcards": "Tarjetas regalo",
  "nav.login": "Entrar", "nav.wishlist": "Lista de deseos", "nav.menu": "Menú",
  "account.title": "Tu cuenta", "account.subtitle": "Gestiona tu cuenta de NexKey y tus juegos guardados.",
  "account.email": "Correo electrónico", "account.password": "Contraseña", "account.confirm": "Confirmar contraseña",
  "account.password.hint": "Mínimo 8 caracteres.",
  "account.login.title": "Iniciar sesión", "account.login.subtitle": "Accede a tus juegos guardados desde cualquier dispositivo.",
  "account.login.cta": "Iniciar sesión", "account.login.noAccount": "¿Aún no tienes cuenta?",
  "account.register.title": "Crear cuenta", "account.register.subtitle": "Es gratis y solo lleva unos segundos.",
  "account.register.cta": "Crear cuenta", "account.register.haveAccount": "¿Ya tienes una cuenta?",
  "account.logout": "Cerrar sesión", "account.loading": "Cargando…", "account.memberSince": "Miembro desde",
  "account.error.required": "Introduce tu correo y contraseña.",
  "account.error.short": "La contraseña debe tener al menos 8 caracteres.",
  "account.error.mismatch": "Las contraseñas no coinciden.",
  "account.error.login": "Error al iniciar sesión.", "account.error.register": "Error al crear la cuenta.",
  "wishlist.count": "{count} juegos guardados", "wishlist.empty": "Todavía no has guardado ningún juego.",
  "wishlist.syncHint": "Inicia sesión para sincronizar tu lista",
  "wishlist.sort": "Ordenar", "wishlist.sort.discount": "Mayor descuento", "wishlist.sort.priceAsc": "Precio más bajo",
  "wishlist.sort.priceDesc": "Precio más alto", "wishlist.sort.title": "Título A–Z",
  "wishlist.open": "Abrir juego", "wishlist.remove": "Quitar", "wishlist.bestOffer": "Mejor oferta",
  "wishlist.reference": "Precio en Steam", "wishlist.stores": "{count} tiendas", "wishlist.store": "1 tienda",
  "wishlist.noOffers": "Aún sin ofertas", "wishlist.alertActive": "Alerta: por debajo de {price}", "wishlist.alertHit": "Objetivo alcanzado",
  "account.dashboard.wishlist": "Juegos guardados", "account.dashboard.alerts": "Alertas de precio activas",
  "account.dashboard.viewWishlist": "Abrir lista de deseos", "account.dashboard.viewAlerts": "Gestionar alertas",
  "account.settings": "Ajustes de la cuenta", "account.settings.hint": "El idioma, la moneda y el tema se guardan en este dispositivo.",
  "account.settings.language": "Idioma", "account.settings.currency": "Moneda", "account.settings.theme": "Tema",
  "account.settings.dark": "Oscuro", "account.settings.light": "Claro", "account.settings.soon": "El cambio de contraseña y de correo llegará pronto.",
  "account.back": "Volver a la cuenta",
  "alerts.title": "Alertas de precio", "alerts.subtitle": "Te avisaremos cuando un juego baje de tu precio objetivo.",
  "alerts.button": "Alerta de precio", "alerts.button.active": "Alerta activa",
  "alerts.dialog.title": "Crear alerta de precio", "alerts.dialog.current": "Mejor precio actual",
  "alerts.dialog.currency": "Moneda", "alerts.dialog.target": "Avísame cuando el precio baje de",
  "alerts.dialog.create": "Crear alerta", "alerts.dialog.save": "Guardar cambios", "alerts.dialog.delete": "Eliminar alerta",
  "alerts.dialog.disable": "Desactivar", "alerts.dialog.activeNote": "Tu alerta está activa — objetivo {price}.",
  "alerts.dialog.noticeSoon": "Las notificaciones por correo llegarán pronto; tu alerta ya está guardada.",
  "alerts.dialog.invalid": "Introduce un precio válido.", "alerts.dialog.saved": "Alerta guardada.", "alerts.dialog.removed": "Alerta eliminada.",
  "alerts.loginHint": "Inicia sesión para crear alertas de precio.",
  "alerts.current": "Precio actual", "alerts.target": "Objetivo", "alerts.difference": "Diferencia",
  "alerts.status": "Estado", "alerts.status.active": "Activa", "alerts.status.hit": "Objetivo alcanzado", "alerts.status.paused": "Pausada",
  "alerts.edit": "Editar", "alerts.delete": "Eliminar", "alerts.openGame": "Abrir juego",
  "alerts.empty": "Todavía no tienes alertas de precio.", "alerts.empty.cta": "Explorar juegos",
  "alerts.count": "{count} alertas activas", "alerts.noPrice": "Sin precio aún",
  "search.placeholder": "Buscar un juego…",
  "hero.badge": "Comparación de precios en tiempo real · Actualizado a diario",
  "hero.title.a": "Compara precios de juegos.", "hero.title.b": "Encuentra la mejor oferta.", "hero.title.c": "",
  "hero.subtitle": "Una búsqueda en todas las grandes tiendas. Descubre al instante la clave legal más barata y ahorra en cada compra.",
  "hero.searchBtn": "Buscar",
  "hero.browseGames": "Explorar juegos",
  "hero.bestDeals": "Mejores ofertas de hoy",
  "hero.searchPlaceholder": "Busca entre más de {count} juegos…",
  "hero.popular": "Populares:",
  "hero.stat.games": "Juegos", "hero.stat.stores": "Tiendas", "hero.stat.offers": "Ofertas activas",
  "home.honest.title": "Comparador honesto de claves de juego",
  "home.honest.body": "NexKey.gg rastrea el precio de referencia oficial de Steam para cada juego importado y lo compara con las ofertas de tiendas de claves de confianza. Steam es nuestra fuente de verdad: títulos, descripciones, capturas y precios base provienen directamente de la API de la tienda de Steam. No estamos afiliados con Valve Corporation.",
  "home.honest.link": "Leer el aviso de Steam",
  "section.trending": "En tendencia", "section.new": "Novedades",
  "section.deals": "Mejores ofertas", "section.genres": "Géneros populares", "section.shops": "Tiendas destacadas",
  "empty.games": "Aún no se han importado juegos. Inicia una importación de Steam en el panel.",
  "empty.offers": "Aún no hay ofertas de tiendas.",
  "empty.steamPrice": "Precio de referencia de Steam no disponible.",
  "empty.deals": "Aún no hay ofertas — añade ofertas desde el panel.",
  "empty.shops": "Aún no hay tiendas configuradas.",
  "empty.genres": "Aún no hay géneros — se crean al importar juegos.",
  "offer.viewDeal": "Ver oferta", "offer.bestPrice": "Mejor precio",
  "offer.steamPrice": "Precio Steam", "offer.savings": "Ahorras",
  "loading": "Cargando…", "common.error": "Algo salió mal.",
  "common.retry": "Reintentar", "common.goHome": "Ir al inicio",

  "footer.tagline": "Comparador independiente de precios de claves de juego. Seguimos los precios de referencia de Steam a diario y los comparamos con tiendas de confianza para que siempre pagues el precio justo.",
  "footer.explore": "Explorar", "footer.company": "Empresa", "footer.disclosures": "Avisos",
  "footer.about": "Acerca", "footer.contact": "Contacto", "footer.privacy": "Privacidad", "footer.terms": "Términos",
  "footer.affiliate": "Aviso de afiliación", "footer.steam": "Aviso Steam",
  "footer.copyright": "Sin afiliación con Valve Corporation. Steam y el logo de Steam son marcas de Valve.",

  "game.breadcrumb.home": "Inicio", "game.breadcrumb.games": "Juegos",
  "game.meta.releaseDate": "Fecha de lanzamiento", "game.meta.developer": "Desarrollador",
  "game.meta.publisher": "Editor", "game.meta.platforms": "Plataformas",
  "game.meta.metacritic": "Metacritic", "game.meta.genres": "Géneros", "game.meta.tags": "Etiquetas Steam",
  "game.action.wishlist": "Lista de deseos", "game.action.share": "Compartir",
  "game.action.comparePrices": "Comparar precios",
  "game.action.readMore": "Leer más", "game.action.readLess": "Mostrar menos",
  "game.action.buy": "Comprar", "game.action.buyNow": "Comprar ahora", "game.action.buyOnSteam": "Comprar en Steam",
  "game.section.priceComparison": "Comparación de precios",
  "game.section.priceComparison.sub": "Ordenado por precio más bajo. Incluye Steam y todas las tiendas de claves listadas.",
  "game.section.quickFacts": "Información rápida",
  "game.section.about": "Sobre este juego",
  "game.section.steamDesc": "Descripción oficial de Steam",
  "game.section.media": "Capturas y vídeos",
  "game.section.features": "Características",
  "game.section.languages": "Idiomas compatibles",
  "game.section.languages.showAll": "Mostrar todos los idiomas",
  "game.section.languages.showLess": "Mostrar menos",
  "game.section.additional": "Información adicional",
  "game.section.system": "Requisitos del sistema",
  "game.additional.metacritic": "Puntuación Metacritic",
  "game.additional.controller": "Soporte de mando",
  "game.additional.achievements": "Logros",
  "game.additional.dlc": "DLC disponible",
  "game.additional.packages": "Ediciones / paquetes",
  "game.additional.website": "Sitio oficial",
  "game.additional.legal": "Aviso legal",
  "game.table.language": "Idioma", "game.table.interface": "Interfaz",
  "game.table.audio": "Audio completo", "game.table.subtitles": "Subtítulos",
  "game.verified": "Tiendas verificadas",
  "game.priceNote": "Descuentos comparados con el precio de referencia de Steam.",
  "game.affiliate": "Aviso de afiliación",
  "history.title": "Historial de precios", "history.best": "Mejor precio", "history.current": "Precio actual",
  "history.lowest": "Precio más bajo", "history.since": "Registrado desde", "history.shops": "Tiendas registradas",
  "history.range.7d": "7 días", "history.range.30d": "30 días", "history.range.90d": "90 días", "history.range.all": "Histórico",
  "history.empty": "Aún no hay historial de precios para este juego.",
  "history.error": "El historial de precios no está disponible ahora mismo.",
  "game.similar.title": "También te puede gustar",
  "game.similar.sub": "Jugadores interesados en este juego también vieron",
  "game.similar.empty": "Aún no hay recomendaciones disponibles.",
  "game.similar.bestDeal": "Mejor oferta",
  "game.quick.metacritic": "Metacritic", "game.quick.release": "Lanzamiento",
  "game.quick.platform": "Plataforma / DRM", "game.quick.languages": "Idiomas",
  "game.quick.achievements": "Logros", "game.quick.controller": "Mando",
  "game.quick.dlc": "DLC", "game.quick.developer": "Desarrollador", "game.quick.publisher": "Editor",
  "game.tabs.overview": "Resumen", "game.tabs.system": "Requisitos del sistema",
  "game.tabs.languages": "Idiomas", "game.tabs.features": "Características",
  "game.tabs.additional": "Información adicional",

  "reqs.minimum": "Mínimo", "reqs.recommended": "Recomendado",
  "reqs.empty": "Los requisitos del sistema para esta plataforma no están disponibles.",
  "reqs.label.os": "Sistema operativo", "reqs.label.processor": "Procesador",
  "reqs.label.memory": "Memoria", "reqs.label.graphics": "Gráficos",
  "reqs.label.directx": "DirectX", "reqs.label.storage": "Almacenamiento",
  "reqs.label.sound": "Tarjeta de sonido", "reqs.label.network": "Red",
  "reqs.label.vr": "Soporte VR", "reqs.label.notes": "Notas adicionales",

  "price.rank": "#", "price.store": "Tienda", "price.edition": "Edición",
  "price.platform": "Plataforma", "price.region": "Región", "price.drm": "DRM",
  "price.delivery": "Entrega", "price.price": "Precio", "price.savings": "Ahorro",
  "price.bestDeal": "Mejor oferta", "price.officialStore": "Tienda oficial",
  "shopType.official-store": "Tienda oficial", "shopType.authorized-retailer": "Minorista autorizado",
  "shopType.marketplace": "Mercado", "shopType.key-reseller": "Revendedor de claves",
  "price.instantDelivery": "Disponible al instante",
  "price.unknownDrm": "DRM desconocido",
  "price.lowest": "Precio más bajo", "price.steamPrice": "Precio Steam",
  "price.youSave": "Ahorras",
  "price.topOffers": "Mejores ofertas", "price.activeEdition": "Para la edición seleccionada", "game.dlcs": "DLCs", "game.dlc.badge": "DLC / Expansión", "game.dlc.requires": "Requiere", "game.dlc.from": "desde", "game.dlc.offers": "ofertas",
  "price.viewAllOffers": "Ver todas las ofertas ({count})",
  "price.updatedNote": "Precios actualizados automáticamente en cada tienda verificada.",
  "price.verifiedPartner": "Socio verificado",
  "price.from": "desde",
  "price.regular": "Precio normal de la tienda",
  "price.withCode": "Con el código",
  "price.couponBadge": "cupón",
  "price.copyCode": "Copiar código",
  "price.copied": "¡Copiado!",
  "price.couponRequired": "Cupón obligatorio",
  "prefs.title": "Personaliza tus resultados de precios",
  "prefs.subtitle": "Elige las regiones y tiendas que quieres ver. Puedes cambiar estas preferencias más tarde.",
  "prefs.regions": "Regiones", "prefs.shops": "Tiendas", "prefs.all": "Todas",
  "prefs.unknown.title": "¿Qué significa «Desconocida»?",
  "prefs.unknown.body": "Desconocida significa que no pudimos determinar con fiabilidad la región de la clave a partir de los datos disponibles. Una oferta Desconocida puede seguir siendo Global, Europa, América, China u otra región. Recomendamos mantener Desconocida activada para no perderte ofertas potencialmente relevantes. Comprueba siempre la página del producto y las restricciones de activación del vendedor antes de comprar.",
  "prefs.save": "Guardar preferencias",
  "prefs.continueAll": "Continuar con todas las regiones y tiendas",
  "prefs.open": "Regiones y tiendas",
  "prefs.note": "Guardado solo en este dispositivo.",
};

const PL: Record<string, string> = {
  "nav.games": "Gry", "nav.deals": "Najlepsze okazje", "nav.genres": "Gatunki", "nav.shops": "Sklepy", "nav.giftcards": "Karty podarunkowe",
  "nav.login": "Zaloguj", "nav.wishlist": "Lista życzeń", "nav.menu": "Menu",
  "account.title": "Twoje konto", "account.subtitle": "Zarządzaj kontem NexKey i zapisanymi grami.",
  "account.email": "E-mail", "account.password": "Hasło", "account.confirm": "Potwierdź hasło",
  "account.password.hint": "Minimum 8 znaków.",
  "account.login.title": "Zaloguj się", "account.login.subtitle": "Dostęp do zapisanych gier z każdego urządzenia.",
  "account.login.cta": "Zaloguj się", "account.login.noAccount": "Nie masz jeszcze konta?",
  "account.register.title": "Załóż konto", "account.register.subtitle": "To darmowe i zajmuje kilka sekund.",
  "account.register.cta": "Załóż konto", "account.register.haveAccount": "Masz już konto?",
  "account.logout": "Wyloguj", "account.loading": "Ładowanie…", "account.memberSince": "Członek od",
  "account.error.required": "Podaj e-mail i hasło.",
  "account.error.short": "Hasło musi mieć co najmniej 8 znaków.",
  "account.error.mismatch": "Hasła nie są takie same.",
  "account.error.login": "Logowanie nie powiodło się.", "account.error.register": "Rejestracja nie powiodła się.",
  "wishlist.count": "{count} zapisanych gier", "wishlist.empty": "Nie zapisałeś jeszcze żadnych gier.",
  "wishlist.syncHint": "Zaloguj się, aby zsynchronizować listę",
  "wishlist.sort": "Sortuj", "wishlist.sort.discount": "Największa zniżka", "wishlist.sort.priceAsc": "Najniższa cena",
  "wishlist.sort.priceDesc": "Najwyższa cena", "wishlist.sort.title": "Tytuł A–Z",
  "wishlist.open": "Otwórz grę", "wishlist.remove": "Usuń", "wishlist.bestOffer": "Najlepsza oferta",
  "wishlist.reference": "Cena Steam", "wishlist.stores": "{count} sklepów", "wishlist.store": "1 sklep",
  "wishlist.noOffers": "Brak ofert", "wishlist.alertActive": "Alert: poniżej {price}", "wishlist.alertHit": "Cel osiągnięty",
  "account.dashboard.wishlist": "Zapisane gry", "account.dashboard.alerts": "Aktywne alerty cenowe",
  "account.dashboard.viewWishlist": "Otwórz listę życzeń", "account.dashboard.viewAlerts": "Zarządzaj alertami",
  "account.settings": "Ustawienia konta", "account.settings.hint": "Język, waluta i motyw są zapisywane na tym urządzeniu.",
  "account.settings.language": "Język", "account.settings.currency": "Waluta", "account.settings.theme": "Motyw",
  "account.settings.dark": "Ciemny", "account.settings.light": "Jasny", "account.settings.soon": "Zmiana hasła i adresu e-mail pojawi się wkrótce.",
  "account.back": "Wróć do konta",
  "alerts.title": "Alerty cenowe", "alerts.subtitle": "Powiadomimy cię, gdy gra spadnie poniżej twojej ceny docelowej.",
  "alerts.button": "Alert cenowy", "alerts.button.active": "Alert aktywny",
  "alerts.dialog.title": "Ustaw alert cenowy", "alerts.dialog.current": "Aktualnie najlepsza cena",
  "alerts.dialog.currency": "Waluta", "alerts.dialog.target": "Powiadom mnie, gdy cena spadnie poniżej",
  "alerts.dialog.create": "Utwórz alert", "alerts.dialog.save": "Zapisz zmiany", "alerts.dialog.delete": "Usuń alert",
  "alerts.dialog.disable": "Wyłącz", "alerts.dialog.activeNote": "Twój alert jest aktywny — cel {price}.",
  "alerts.dialog.noticeSoon": "Powiadomienia e-mail pojawią się wkrótce; alert jest już zapisany.",
  "alerts.dialog.invalid": "Podaj prawidłową cenę.", "alerts.dialog.saved": "Alert zapisany.", "alerts.dialog.removed": "Alert usunięty.",
  "alerts.loginHint": "Zaloguj się, aby tworzyć alerty cenowe.",
  "alerts.current": "Aktualna cena", "alerts.target": "Cel", "alerts.difference": "Różnica",
  "alerts.status": "Status", "alerts.status.active": "Aktywny", "alerts.status.hit": "Cel osiągnięty", "alerts.status.paused": "Wstrzymany",
  "alerts.edit": "Edytuj", "alerts.delete": "Usuń", "alerts.openGame": "Otwórz grę",
  "alerts.empty": "Nie masz jeszcze alertów cenowych.", "alerts.empty.cta": "Przeglądaj gry",
  "alerts.count": "{count} aktywnych alertów", "alerts.noPrice": "Brak ceny",
  "search.placeholder": "Szukaj gry…",
  "hero.badge": "Porównanie cen w czasie rzeczywistym · Aktualizowane codziennie",
  "hero.title.a": "Porównaj ceny gier.", "hero.title.b": "Znajdź najlepszą ofertę.", "hero.title.c": "",
  "hero.subtitle": "Jedno wyszukiwanie we wszystkich największych sklepach. Błyskawicznie znajdź najtańszy legalny klucz i oszczędzaj na każdej grze.",
  "hero.searchBtn": "Szukaj",
  "hero.browseGames": "Przeglądaj gry",
  "hero.bestDeals": "Najlepsze okazje dnia",
  "hero.searchPlaceholder": "Szukaj wśród ponad {count} gier…",
  "hero.popular": "Popularne:",
  "hero.stat.games": "Gry", "hero.stat.stores": "Sklepy", "hero.stat.offers": "Aktywne oferty",
  "home.honest.title": "Uczciwe porównanie cen kluczy do gier",
  "home.honest.body": "NexKey.gg śledzi oficjalną cenę referencyjną Steam dla każdej zaimportowanej gry i porównuje ją z ofertami zaufanych sklepów z kluczami. Steam jest naszym źródłem prawdy — tytuły, opisy, zrzuty ekranu i ceny bazowe pochodzą bezpośrednio z API Steam Storefront. Nie jesteśmy powiązani z Valve Corporation.",
  "home.honest.link": "Przeczytaj zastrzeżenie Steam",
  "section.trending": "Na czasie", "section.new": "Nowości",
  "section.deals": "Najlepsze okazje", "section.genres": "Popularne gatunki", "section.shops": "Polecane sklepy",
  "empty.games": "Nie zaimportowano jeszcze gier. Uruchom import Steam w panelu administratora.",
  "empty.offers": "Brak dostępnych ofert sklepów.",
  "empty.steamPrice": "Cena referencyjna Steam obecnie niedostępna.",
  "empty.deals": "Brak okazji — dodaj oferty w panelu administratora.",
  "empty.shops": "Nie skonfigurowano jeszcze żadnych sklepów.",
  "empty.genres": "Brak gatunków — są tworzone automatycznie podczas importu gier.",
  "offer.viewDeal": "Zobacz ofertę", "offer.bestPrice": "Najlepsza cena",
  "offer.steamPrice": "Cena Steam", "offer.savings": "Oszczędzasz",
  "loading": "Ładowanie…", "common.error": "Coś poszło nie tak.",
  "common.retry": "Spróbuj ponownie", "common.goHome": "Strona główna",

  "footer.tagline": "Niezależne porównanie cen kluczy do gier. Codziennie śledzimy ceny referencyjne Steam i porównujemy je z zaufanymi sklepami, abyś zawsze płacił uczciwą cenę.",
  "footer.explore": "Odkrywaj", "footer.company": "Firma", "footer.disclosures": "Informacje",
  "footer.about": "O nas", "footer.contact": "Kontakt", "footer.privacy": "Prywatność", "footer.terms": "Regulamin",
  "footer.affiliate": "Informacja o afiliacji", "footer.steam": "Zastrzeżenie Steam",
  "footer.copyright": "Niezwiązane z Valve Corporation. Steam i logo Steam są znakami towarowymi firmy Valve.",

  "game.breadcrumb.home": "Start", "game.breadcrumb.games": "Gry",
  "game.meta.releaseDate": "Data wydania", "game.meta.developer": "Producent",
  "game.meta.publisher": "Wydawca", "game.meta.platforms": "Platformy",
  "game.meta.metacritic": "Metacritic", "game.meta.genres": "Gatunki", "game.meta.tags": "Tagi Steam",
  "game.action.wishlist": "Lista życzeń", "game.action.share": "Udostępnij",
  "game.action.comparePrices": "Porównaj ceny",
  "game.action.readMore": "Czytaj więcej", "game.action.readLess": "Pokaż mniej",
  "game.action.buy": "Kup", "game.action.buyNow": "Kup teraz", "game.action.buyOnSteam": "Kup na Steam",
  "game.section.priceComparison": "Porównanie cen",
  "game.section.priceComparison.sub": "Posortowane według najniższej ceny. Zawiera Steam i wszystkie wymienione sklepy z kluczami.",
  "game.section.quickFacts": "Skrót informacji",
  "game.section.about": "O tej grze",
  "game.section.steamDesc": "Oficjalny opis Steam",
  "game.section.media": "Zrzuty i filmy",
  "game.section.features": "Cechy gry",
  "game.section.languages": "Obsługiwane języki",
  "game.section.languages.showAll": "Pokaż wszystkie języki",
  "game.section.languages.showLess": "Pokaż mniej",
  "game.section.additional": "Dodatkowe informacje",
  "game.section.system": "Wymagania systemowe",
  "game.additional.metacritic": "Ocena Metacritic",
  "game.additional.controller": "Obsługa kontrolera",
  "game.additional.achievements": "Osiągnięcia",
  "game.additional.dlc": "Dostępne DLC",
  "game.additional.packages": "Edycje / pakiety",
  "game.additional.website": "Oficjalna strona",
  "game.additional.legal": "Nota prawna",
  "game.table.language": "Język", "game.table.interface": "Interfejs",
  "game.table.audio": "Pełne audio", "game.table.subtitles": "Napisy",
  "game.verified": "Zweryfikowane sklepy",
  "game.priceNote": "Rabaty porównane z ceną referencyjną Steam.",
  "game.affiliate": "Informacja o afiliacji",
  "history.title": "Historia cen", "history.best": "Najlepsza cena", "history.current": "Aktualna cena",
  "history.lowest": "Najniższa cena", "history.since": "Śledzone od", "history.shops": "Śledzone sklepy",
  "history.range.7d": "7 dni", "history.range.30d": "30 dni", "history.range.90d": "90 dni", "history.range.all": "Cały okres",
  "history.empty": "Brak zapisanej historii cen dla tej gry.",
  "history.error": "Historia cen jest obecnie niedostępna.",
  "game.similar.title": "Może Cię również zainteresować",
  "game.similar.sub": "Gracze zainteresowani tą grą oglądali również",
  "game.similar.empty": "Brak dostępnych rekomendacji.",
  "game.similar.bestDeal": "Najlepsza okazja",
  "game.quick.metacritic": "Metacritic", "game.quick.release": "Premiera",
  "game.quick.platform": "Platforma / DRM", "game.quick.languages": "Języki",
  "game.quick.achievements": "Osiągnięcia", "game.quick.controller": "Kontroler",
  "game.quick.dlc": "DLC", "game.quick.developer": "Producent", "game.quick.publisher": "Wydawca",
  "game.tabs.overview": "Przegląd", "game.tabs.system": "Wymagania systemowe",
  "game.tabs.languages": "Języki", "game.tabs.features": "Funkcje",
  "game.tabs.additional": "Dodatkowe informacje",

  "reqs.minimum": "Minimalne", "reqs.recommended": "Zalecane",
  "reqs.empty": "Wymagania systemowe dla tej platformy nie są dostępne.",
  "reqs.label.os": "System operacyjny", "reqs.label.processor": "Procesor",
  "reqs.label.memory": "Pamięć", "reqs.label.graphics": "Grafika",
  "reqs.label.directx": "DirectX", "reqs.label.storage": "Miejsce na dysku",
  "reqs.label.sound": "Karta dźwiękowa", "reqs.label.network": "Sieć",
  "reqs.label.vr": "Obsługa VR", "reqs.label.notes": "Dodatkowe uwagi",

  "price.rank": "#", "price.store": "Sklep", "price.edition": "Edycja",
  "price.platform": "Platforma", "price.region": "Region", "price.drm": "DRM",
  "price.delivery": "Dostawa", "price.price": "Cena", "price.savings": "Oszczędność",
  "price.bestDeal": "Najlepsza okazja", "price.officialStore": "Oficjalny sklep",
  "shopType.official-store": "Oficjalny sklep", "shopType.authorized-retailer": "Autoryzowany sprzedawca",
  "shopType.marketplace": "Marketplace", "shopType.key-reseller": "Sprzedawca kluczy",
  "price.instantDelivery": "Dostępne od ręki",
  "price.unknownDrm": "Nieznane DRM",
  "price.lowest": "Najniższa cena", "price.steamPrice": "Cena Steam",
  "price.youSave": "Oszczędzasz",
  "price.topOffers": "Najlepsze oferty", "price.activeEdition": "Dla wybranej edycji", "game.dlcs": "DLC", "game.dlc.badge": "DLC / Dodatek", "game.dlc.requires": "Wymaga", "game.dlc.from": "od", "game.dlc.offers": "ofert",
  "price.viewAllOffers": "Zobacz wszystkie oferty ({count})",
  "price.updatedNote": "Ceny aktualizowane automatycznie we wszystkich zweryfikowanych sklepach.",
  "price.verifiedPartner": "Zweryfikowany partner",
  "price.from": "od",
  "price.regular": "Zwykła cena sklepowa",
  "price.withCode": "Z kodem",
  "price.couponBadge": "kupon",
  "price.copyCode": "Kopiuj kod",
  "price.copied": "Skopiowano!",
  "price.couponRequired": "Wymagany kupon",
  "prefs.title": "Dostosuj wyniki cen",
  "prefs.subtitle": "Wybierz regiony i sklepy, które chcesz widzieć. Możesz zmienić te preferencje później.",
  "prefs.regions": "Regiony", "prefs.shops": "Sklepy", "prefs.all": "Wszystkie",
  "prefs.unknown.title": "Co oznacza „Nieznany”?",
  "prefs.unknown.body": "Nieznany oznacza, że nie udało nam się wiarygodnie określić regionu klucza na podstawie dostępnych danych. Oferta Nieznana może nadal być Globalna, Europejska, Amerykańska, Chińska lub inna. Zalecamy pozostawienie opcji Nieznany włączonej, aby nie przegapić potencjalnie trafnych ofert. Przed zakupem zawsze sprawdź stronę produktu i ograniczenia aktywacji sprzedawcy.",
  "prefs.save": "Zapisz preferencje",
  "prefs.continueAll": "Kontynuuj ze wszystkimi regionami i sklepami",
  "prefs.open": "Regiony i sklepy",
  "prefs.note": "Zapisane tylko na tym urządzeniu.",
};

const STRINGS: Record<Locale, Record<string, string>> = { en: EN, de: DE, es: ES, pl: PL };
const SUPPORTED_LOCALES: Locale[] = ["en", "de", "es", "pl"];

/* ============ Currency ============ */

// Supported currency keys (rates themselves live in @/lib/fx and are
// refreshed from the NexKey API; these defaults only cover the first load).
const FX: Record<Currency, number> = DEFAULT_RATES as Record<Currency, number>;

const SUPPORTED_CURRENCIES: Currency[] = ["EUR", "USD", "GBP", "PLN", "CHF", "CAD", "AUD", "JPY"];

// Country → default currency map for browser detection.
const COUNTRY_TO_CURRENCY: Record<string, Currency> = {
  DE: "EUR", AT: "EUR", ES: "EUR", FR: "EUR", IT: "EUR", NL: "EUR", BE: "EUR",
  IE: "EUR", PT: "EUR", FI: "EUR", GR: "EUR", LU: "EUR", SK: "EUR", SI: "EUR",
  EE: "EUR", LV: "EUR", LT: "EUR", MT: "EUR", CY: "EUR", HR: "EUR",
  CH: "CHF", GB: "GBP", UK: "GBP", US: "USD", PL: "PLN", CA: "CAD", AU: "AUD", JP: "JPY",
};

function detectLocale(): Locale {
  if (typeof navigator === "undefined") return "en";
  const langs = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || "en"];
  for (const raw of langs) {
    const l = (raw || "").split("-")[0].toLowerCase() as Locale;
    if (SUPPORTED_LOCALES.includes(l)) return l;
  }
  return "en";
}

function detectCurrency(): Currency {
  if (typeof navigator === "undefined") return "EUR";
  const raw = navigator.language || "en";
  const region = (raw.split("-")[1] || "").toUpperCase();
  return COUNTRY_TO_CURRENCY[region] ?? "EUR";
}

function localeTag(l: Locale): string {
  return l === "en" ? "en-GB" : l === "de" ? "de-DE" : l === "es" ? "es-ES" : "pl-PL";
}

/* ============ Provider ============ */

const Ctx = createContext<SettingsCtx | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>("en");
  const [currency, setCurrencyState] = useState<Currency>("EUR");
  const [theme, setThemeState] = useState<Theme>("dark");
  const [offerPrefs, setOfferPrefsState] = useState<OfferPreferences | null>(null);
  const [offerPrefsLoaded, setOfferPrefsLoaded] = useState(false);
  const [fxRates, setFxRates] = useState<FxRates>(getActiveRates);

  // Load the daily reference rates; keep defaults if the API is unreachable.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/exchange-rates`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled || !body?.data?.rates) return;
        const merged = mergeRates(body.data.rates);
        setActiveRates(merged);
        setFxRates(merged);
      })
      .catch(() => { /* keep last known rates */ });
    return () => { cancelled = true; };
  }, []);

  // Hydrate from localStorage; if unset, detect browser preferences.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const l = window.localStorage.getItem("nexkey_locale") as Locale | null;
    const c = window.localStorage.getItem("nexkey_currency") as Currency | null;
    const th = window.localStorage.getItem("nexkey_theme") as Theme | null;
    if (l && STRINGS[l]) setLocaleState(l);
    else setLocaleState(detectLocale());
    if (c && FX[c]) setCurrencyState(c);
    else setCurrencyState(detectCurrency());
    if (th === "dark" || th === "light") setThemeState(th);
    setOfferPrefsState(loadOfferPreferences());
    setOfferPrefsLoaded(true);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.classList.remove("dark", "light");
    root.classList.add(theme);
    root.style.colorScheme = theme;
  }, [theme]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("lang", locale);
  }, [locale]);

  const value = useMemo<SettingsCtx>(() => ({
    locale, currency, theme,
    offerPrefs,
    offerPrefsLoaded,
    setOfferPrefs: (p) => {
      setOfferPrefsState(p);
      saveOfferPreferences(p);
    },
    setLocale: (l) => {
      setLocaleState(l);
      if (typeof window !== "undefined") window.localStorage.setItem("nexkey_locale", l);
    },
    setCurrency: (c) => {
      setCurrencyState(c);
      if (typeof window !== "undefined") window.localStorage.setItem("nexkey_currency", c);
    },
    setTheme: (t) => {
      setThemeState(t);
      if (typeof window !== "undefined") window.localStorage.setItem("nexkey_theme", t);
    },
    toggleTheme: () => {
      setThemeState((prev) => {
        const next: Theme = prev === "dark" ? "light" : "dark";
        if (typeof window !== "undefined") window.localStorage.setItem("nexkey_theme", next);
        return next;
      });
    },
    t: (key) => STRINGS[locale]?.[key] ?? STRINGS.en[key] ?? key,
    formatPrice: (cents, srcCurrency) => {
      if (cents == null) return "—";
      const converted = convertMinor(cents, srcCurrency || "EUR", currency, fxRates);
      const value = (converted ?? cents) / 100;
      try {
        return new Intl.NumberFormat(localeTag(locale), {
          style: "currency",
          currency,
          maximumFractionDigits: currency === "JPY" ? 0 : 2,
          minimumFractionDigits: currency === "JPY" ? 0 : 2,
        }).format(value);
      } catch {
        return `${value.toFixed(2)} ${currency}`;
      }
    },
    convertPrice: (cents, fromCurrency) => convertMinor(cents, fromCurrency || "EUR", currency, fxRates),
    fxRates,
  }), [locale, currency, theme, offerPrefs, offerPrefsLoaded, fxRates]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSettings() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useSettings must be used inside <SettingsProvider>");
  return v;
}

export { SUPPORTED_LOCALES, SUPPORTED_CURRENCIES };
